import {
  ChangeDetectorRef,
  Component,
  OnInit,
  HostListener,
  inject,
  DestroyRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ProductsService } from 'src/app/services/product.service';
import { Subject, debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProductsEditorComponent } from 'src/app/shared/components/products-editor/products-editor.component';
import { ConfirmLeaveModalComponent } from 'src/app/shared/components/confirm-leave-modal/confirm-leave-modal.component';
import { CatalogTemplatePickerComponent } from 'src/app/components/catalog-template-picker/catalog-template-picker.component';
import { MenuCardComponent } from 'src/app/shared/components/menu-card/menu-card.component';
import { Product, ProductCategory } from 'src/app/models/product.model';
import type { ActivityTypeInfo } from 'src/app/models/cluster.model';
import { HasUnsavedChanges } from 'src/app/guards/unsaved-changes.guard';
import { LogService } from '../../services/log.service';
import { AnalyticsService } from '../../services/analytics.service';

@Component({
  selector: 'app-products',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ProductsEditorComponent,
    ConfirmLeaveModalComponent,
    MenuCardComponent,
  ],
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.css'],
})
export class ProductsComponent implements OnInit, HasUnsavedChanges {
  private log = inject(LogService);
  private analytics = inject(AnalyticsService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private saveDebounce$ = new Subject<void>();
  isSaving = false;

  // Guard pour confirmation de navigation
  confirmLeave$ = new Subject<boolean>();
  showLeaveConfirmation = false;
  hasUnsavedChanges = false;

  merchantProfile = {
    type: 'fixed',
    activityType: 'bakery',
    teamSize: 'solo',
    affluence: 'medium',
  };

  marchandProducts: Product[] = [];
  selectedProduct: Product | null = null;

  showRecommendedShowcase = true;
  catalogExpanded = false;
  notification: { message: string; type: string } | null = null;

  showSearchCatalog = false;
  showCustomProductModal = false;
  showTemplatePicker = false;
  showMenuCard = false;

  // Filtres de recherche
  searchTerm = '';
  selectedCategory = 'all';
  showOnlyNew = false;
  isSearching = false;

  // Observable des résultats
  searchResults$ = this.productsService.searchResults$;

  // Produit personnalisé (formulaire)
  customProduct = {
    name: '',
    description: '',
    category: '',
    purchasePrice: 0,
    quantity: 10,
    unit: 'unité',
    sku: '',
    productIcon: '📦',
  };

  // Icônes de produits
  productIcons = [
    '🥖',
    '🥐',
    '🍞',
    '🥯',
    '🧈',
    '🍫',
    '🍰',
    '🧁',
    '🎂',
    '🍪',
    '🥧',
    '🍩',
    '🍮',
    '🍯',
    '🥨',
    '🥞',
    '☕',
    '🍵',
    '🥤',
    '🧃',
    '🍋',
    '🍎',
    '🍇',
    '🍓',
    '🍒',
    '🍑',
    '🥝',
    '🍌',
    '🍉',
    '🍊',
    '🍕',
    '🍔',
    '🌭',
    '🥪',
    '🌮',
    '🌯',
    '🥗',
    '🍟',
    '🍗',
    '🥓',
    '🍖',
    '🥩',
    '🍤',
    '🦐',
    '🐟',
    '🍱',
    '🍙',
    '🍘',
    '🍚',
    '🍛',
  ];

  // Catégories disponibles
  categories: ProductCategory[] = [];
  units: string[] = [];
  activityTypes: ActivityTypeInfo[] = [];

  // Catalogue recommandé (top 10 basé sur cluster)
  recommendedCatalog: Product[] | null = null;

  // Catalogue complet
  constructor(
    private router: Router,
    private productsService: ProductsService,
  ) {}

  ngOnInit() {
    // Charger les métadonnées dynamiques
    this.productsService
      .loadMeta()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Lecture directe des signaux (compatible zoneless)
        this.categories = this.productsService.categories();
        this.units = this.productsService.units();
        this.activityTypes = this.productsService.activityTypes();

        // initialiser l'unité du produit personnalisé
        this.customProduct.unit = this.units[0] ?? 'unité';
        this.cdr.markForCheck();
      });

    // Charger les produits utilisateur et catalogues
    this.loadProducts();

    this.searchResults$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isSearching = false;
        this.cdr.markForCheck();
      });

    // Auto-save avec debounce 2 secondes
    this.saveDebounce$
      .pipe(debounceTime(2000), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.autoSave();
      });
  }

  onTemplateApplied(event: { templateName: string; productsCreated: number }) {
    this.showTemplatePicker = false;
    this.showNotification(
      `Template "${event.templateName}" appliqué : ${event.productsCreated} produits ajoutés`,
      'success',
    );
    this.loadProducts();
  }

  onProductsChange(products: any[]) {
    this.marchandProducts = products;
    this.hasUnsavedChanges = true;
    this.saveDebounce$.next();
  }

  loadProducts() {
    // charger les produits pré-existants
    this.productsService.getMyProducts().subscribe((products) => {
      this.marchandProducts = products;
      this.cdr.markForCheck();
    });
  }

  addProductFromCatalog(catalogProduct: Product) {
    if (this.isProductAdded(catalogProduct.name)) {
      return;
    }

    const newProduct: Product = {
      ...catalogProduct,
      id: Date.now().toString(),
      quantity: catalogProduct.quantity ?? 0,
      sellPrice: +(catalogProduct.purchasePrice * 1.6).toFixed(2), // 160% par défaut
      isRecommended: false,
      isCustom: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.marchandProducts.push(newProduct);
    this.hasUnsavedChanges = true;
    this.analytics.trackProduitAjoute(catalogProduct.category);
    this.showNotification(`"${catalogProduct.name}" ajouté !`, 'success');
  }

  removeProduct(id: string) {
    const product = this.marchandProducts.find((p) => p.id === id);
    this.marchandProducts = this.marchandProducts.filter((p) => p.id !== id);
    if (product) {
      this.hasUnsavedChanges = true;
      this.analytics.trackProduitSupprime(product.name);
      this.showNotification(`"${product.name}" supprimé`, 'info');
    }
  }

  removeProductByName(name: string) {
    const product = this.marchandProducts.find((p) => p.name === name);
    if (product) {
      this.removeProduct(product.id);
    }
  }

  isProductAdded(name: string): boolean {
    return this.marchandProducts.some((p) => p.name === name);
  }

  toggleCatalogExpanded() {
    this.catalogExpanded = !this.catalogExpanded;
  }

  getTotalValue(): number {
    return this.marchandProducts.reduce(
      (sum, p) => sum + (p.purchasePrice || 0) * p.quantity,
      0,
    );
  }

  getTotalQuantity(): number {
    return this.marchandProducts.reduce((sum, p) => sum + p.quantity, 0);
  }

  getUniqueCategories(): number {
    const categories = new Set(this.marchandProducts.map((p) => p.category));
    return categories.size;
  }

  showNotification(message: string, type: string) {
    this.notification = { message, type };
    setTimeout(() => (this.notification = null), 3000);
  }

  saveProducts() {
    this.isSaving = true;
    this.productsService.saveMyProducts(this.marchandProducts).subscribe({
      next: () => {
        this.hasUnsavedChanges = false;
        this.isSaving = false;
        this.analytics.trackProduitModifie();
        this.showNotification(
          '✅ Produits enregistrés avec succès !',
          'success',
        );
        this.cdr.markForCheck();
      },
      error: () => {
        this.isSaving = false;
        this.showNotification('❌ Erreur lors de la sauvegarde', 'error');
        this.cdr.markForCheck();
      },
    });
  }

  cancel() {
    this.router.navigate(['/dashboard']);
  }

  loadRecommendedCatalog() {
    this.productsService.getRecommendedCatalog().subscribe((products) => {
      this.recommendedCatalog = products;
      this.cdr.markForCheck();
    });
  }

  selectProduct(product: Product) {
    this.selectedProduct = { ...product };
  }

  onProductSelected(product: Product) {
    this.selectedProduct = { ...product };
  }

  onProductFieldChange() {
    if (!this.selectedProduct) return;
    const index = this.marchandProducts.findIndex(
      (p) => p.id === this.selectedProduct!.id,
    );
    if (index !== -1) {
      this.marchandProducts[index] = { ...this.selectedProduct };
      // Créer un nouveau tableau pour déclencher ngOnChanges dans le composant enfant
      this.marchandProducts = [...this.marchandProducts];
      this.hasUnsavedChanges = true;
      this.saveDebounce$.next();
    }
  }

  autoSave() {
    if (!this.hasUnsavedChanges || this.isSaving) return;

    this.isSaving = true;
    this.productsService.saveMyProducts(this.marchandProducts).subscribe({
      next: () => {
        this.hasUnsavedChanges = false;
        this.isSaving = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isSaving = false;
        // En cas d'erreur, on garde hasUnsavedChanges = true pour réessayer
        this.log.warn(
          'Auto-save échoué, les modifications seront sauvegardées au prochain essai',
        );
        this.cdr.markForCheck();
      },
    });
  }

  getMargin(product: Product): number {
    if (!product.purchasePrice || product.purchasePrice === 0) return 0;
    return (
      ((product.sellPrice - product.purchasePrice) / product.purchasePrice) *
      100
    );
  }

  openAddCustomProductModal() {
    this.showCustomProductModal = true;
    this.resetCustomProduct();
  }

  toggleSearchCatalog() {
    this.showSearchCatalog = !this.showSearchCatalog;
    if (this.showSearchCatalog) {
      this.resetFilters();
    }
  }

  // Recherche avec RxJS
  onSearchChange(term: string) {
    this.isSearching = true;
    this.productsService.updateSearchFilters({ searchTerm: term });
  }

  onCategoryChange(category: string) {
    this.isSearching = true;
    this.productsService.updateSearchFilters({ category });
  }

  onNewFilterChange(isNew: boolean) {
    this.isSearching = true;
    this.productsService.updateSearchFilters({ isNew: isNew || undefined });
  }

  resetFilters() {
    this.searchTerm = '';
    this.selectedCategory = 'all';
    this.showOnlyNew = false;
    this.isSearching = true;
    this.productsService.updateSearchFilters({
      searchTerm: '',
      category: 'all',
      isNew: undefined,
    });
  }

  // Produits personnalisés
  closeAddCustomProductModal() {
    this.showCustomProductModal = false;
    this.resetCustomProduct();
  }

  resetCustomProduct() {
    this.customProduct = {
      name: '',
      description: '',
      category: '',
      purchasePrice: 0,
      quantity: 10,
      unit: 'unité',
      sku: '',
      productIcon: '📦',
    };
  }

  isCustomProductValid(): boolean {
    return !!(
      this.customProduct.name &&
      this.customProduct.category &&
      this.customProduct.quantity > 0 &&
      this.customProduct.purchasePrice >= 0
    );
  }

  addCustomProduct() {
    if (!this.isCustomProductValid()) return;

    const now = new Date();

    const newProduct: Product = {
      id: Date.now().toString(),
      name: this.customProduct.name,
      description: this.customProduct.description ?? '',
      category: this.customProduct.category,
      purchasePrice: this.customProduct.purchasePrice,
      sellPrice: +(this.customProduct.purchasePrice * 1.6).toFixed(2), // to adapt OR marge par défaut
      quantity: this.customProduct.quantity,
      unit: this.customProduct.unit,
      sku: this.customProduct.sku ?? '',
      productIcon: this.customProduct.productIcon,
      popularity: 0,
      isCustom: true,
      isRecommended: false,
      createdAt: now,
      updatedAt: now,
    };

    this.marchandProducts.push(newProduct);
    this.analytics.trackProduitAjoute('custom');

    this.showNotification(
      `"${newProduct.name}" ajouté avec succès !`,
      'success',
    );

    this.closeAddCustomProductModal();
  }

  // ================================
  // Guard: Confirmation de navigation
  // ================================

  /**
   * Empêche la fermeture/rechargement du navigateur si changements non sauvegardés
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = ''; // Requis pour Chrome
    }
  }

  /**
   * Appelé quand l'utilisateur confirme quitter sans sauvegarder
   */
  onConfirmLeave(): void {
    this.showLeaveConfirmation = false;
    this.confirmLeave$.next(true);
  }

  /**
   * Appelé quand l'utilisateur annule et reste sur la page
   */
  onCancelLeave(): void {
    this.showLeaveConfirmation = false;
    this.confirmLeave$.next(false);
  }
}
